import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { onUnauthorized } from "../api/authEvents";
import { useApiClient } from "../api/ApiProvider";
import type { Session } from "../api/client";
import { clearCachedSession, readCachedSession, writeCachedSession } from "../api/sessionCache";

interface AuthContextValue {
  session: Session | null;
  isAuthenticating: boolean;
  authError: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Real auth (charter C-3 local bootstrap; C-5 OIDC/SSO later) sets a
 * server-side httpOnly session cookie on `login()` — the browser/this code
 * never holds a bearer credential. `session` here is just the non-secret
 * profile the server returned (userId/displayName/role), restored from
 * `sessionCache` on mount so a reload doesn't force a needless re-login: the
 * real backend has no `GET /session`/`/me` endpoint yet to recover "who is
 * this" from the cookie alone (see sessionCache.ts for the tradeoff). This
 * state is never authoritative — the server enforces auth on every request
 * regardless of what it says — and any 401 from an authenticated call (see
 * authEvents.ts) clears it, which sends `ProtectedRoute` back to `/login`
 * reactively.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const [session, setSession] = useState<Session | null>(() => readCachedSession());
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(
    () =>
      onUnauthorized(() => {
        setSession(null);
        clearCachedSession();
      }),
    [],
  );

  const login = useCallback(
    async (username: string, password: string) => {
      setIsAuthenticating(true);
      setAuthError(null);
      try {
        const result = await client.login({ username, password });
        setSession(result);
        writeCachedSession(result);
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : "Sign-in failed. Try again.");
        throw err;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [client],
  );

  const logout = useCallback(async () => {
    await client.logout();
    setSession(null);
    clearCachedSession();
  }, [client]);

  const value = useMemo(
    () => ({ session, isAuthenticating, authError, login, logout }),
    [session, isAuthenticating, authError, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
